import React, { useState, useEffect } from 'react';
import { X, PawPrint, FileText, Calendar, AlertCircle } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { API_BASE_URL } from '../../config/api';
import { APP_NAME } from '../../config/branding';
import { getAuthHeaders } from '../../utils/authUtils';

interface AISummaryModalProps {
    petId: string;
    onClose: () => void;
}

interface MedicalRecord {
    id: string;
    visit_date: string;
    description?: string;
    title: string;
    record_type: string;
    diagnosis?: string;
}

const AISummaryModal: React.FC<AISummaryModalProps> = ({ petId, onClose }) => {
    useEscapeKey(onClose, true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [record, setRecord] = useState<MedicalRecord | null>(null);

    useEffect(() => {
        fetchLatestRecord();
    }, [petId]);

    const fetchLatestRecord = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch medical records for the pet, requesting only the latest one if possible
            // The API supports latest_only=true
            const response = await fetch(
                `${API_BASE_URL}/api/v1/medical-records/pet/${petId}?latest_only=true`,
                {
                    headers: getAuthHeaders(),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch medical records');
            }

            const data = await response.json();

            // The API returns MedicalRecordListResponse with 'records' array
            if (data.records && data.records.length > 0) {
                setRecord(data.records[0]);
            } else {
                setRecord(null);
            }
        } catch (err: any) {
            console.error('Error fetching medical record:', err);
            setError(err.message || 'Failed to load summary');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[80vh] animate-fade-in">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-border">
                    <div className="flex items-center gap-3">
                        <div className="bg-orange-100 dark:bg-orange-900/30 p-2 rounded-lg">
                            <PawPrint className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">{APP_NAME} Medical Summary</h2>
                            <p className="text-sm text-gray-500 dark:text-dark-text-muted">Latest medical record insights</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-hover rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                            <p className="text-gray-500 dark:text-dark-text-muted">Generating summary...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Error loading summary</h3>
                                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{error}</p>
                            </div>
                        </div>
                    ) : record ? (
                        <div className="space-y-6">
                            {/* Description / Summary */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    Medical Description
                                </h3>
                                <div className="bg-blue-50/50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-5 text-gray-800 dark:text-dark-text-primary leading-relaxed whitespace-pre-wrap">
                                    {record.description || (
                                        <span className="text-gray-500 dark:text-dark-text-muted italic">No description available for this record.</span>
                                    )}
                                </div>
                            </div>

                            {/* Diagnosis if available */}
                            {record.diagnosis && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary uppercase tracking-wider mb-3">
                                        Diagnosis
                                    </h3>
                                    <div className="bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-xl p-5 text-gray-800 dark:text-dark-text-primary">
                                        {record.diagnosis}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-50 dark:bg-dark-bg-tertiary rounded-xl border-2 border-dashed border-gray-200 dark:border-dark-border">
                            <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">No Medical Records Found</h3>
                            <p className="text-gray-500 dark:text-dark-text-muted max-w-xs mx-auto mt-1">
                                There are no previous medical records available for this pet to summarize.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-tertiary rounded-b-xl flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text-primary font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors shadow-sm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AISummaryModal;
